import React, { useState, useEffect } from 'react';
import {
  Typography, Button, Container, Box, GridLegacy as Grid, Card, CardContent,
  Chip, Paper, useTheme, styled, alpha
} from '@mui/material';
import { Star, AutoAwesome, LightMode, PlayArrow, Check, Login } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import DemoModal from '../components/DemoModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const showDemo = false;
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '90vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.dark} 0%, 
    ${theme.palette.secondary.dark} 50%, 
    ${theme.palette.primary.main} 100%)`,
  overflow: 'hidden',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.warning.main} 30%, ${theme.palette.warning.dark} 90%)`,
  color: theme.palette.common.black,
  fontWeight: 'bold',
  padding: '12px 32px',
  borderRadius: '50px',
  textTransform: 'none',
  fontSize: '1.1rem',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.warning.light} 30%, ${theme.palette.warning.main} 90%)`,
    transform: 'scale(1.05)',
  },
  transition: 'all 0.3s ease',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.3)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  borderRadius: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: theme.palette.warning.main,
    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
  },
}));

const PricingCard = styled(Card)<{ featured?: boolean }>(({ theme, featured }) => ({
  background: featured
    ? `linear-gradient(135deg, 
        ${alpha(theme.palette.primary.main, 0.4)} 0%, 
        ${alpha(theme.palette.secondary.main, 0.4)} 100%)`
    : `linear-gradient(135deg, 
        ${alpha(theme.palette.grey[800], 0.4)} 0%, 
        ${alpha(theme.palette.grey[900], 0.4)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: featured
    ? `2px solid ${theme.palette.warning.main}`
    : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
  borderRadius: '24px',
  position: 'relative',
  transform: featured ? 'scale(1.05)' : 'none',
  transition: 'all 0.3s ease',
}));

const Home: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [currentTestimonial, setCurrentTestimonial] = useState<number>(0);
  const { language } = useLanguage();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const navigate = useNavigate();
  const testimonials = t('home.testimonials', { returnObjects: true }) as Array<{ text: string; author: string }>;
  const { user } = useAuth();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const handleStartPlan = (index: number) => {
    if (index === 0) {
      navigate('/onboarding');
    } else if (index === 1) {
      navigate('/subscription-management');
    } else {
      navigate('/subscription-management');
    }
  };

  return (
    <>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Chip
            icon={<AutoAwesome />}
            label={t('home.hero.poweredByAI')}
            sx={{
              mb: 4,
              background: alpha(theme.palette.primary.main, 0.3),
              backdropFilter: 'blur(10px)',
              color: 'white',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              direction: 'ltr'
            }}
          />

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
              fontWeight: 'bold',
              mb: 3,
              background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.light}, ${theme.palette.primary.light})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {t('home.hero.title')}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 4,
              color: alpha(theme.palette.primary.light, 0.9)
            }}
          >
            {t('home.hero.subtitle')}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
              color: alpha(theme.palette.common.white, 0.8)
            }}
          >
            {t('home.hero.description')}
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            mb: 3
          }}>
            <GradientButton
              onClick={() => navigate('/onboarding')}
              size="large"
              sx={{
                fontSize: '1.2rem',
                py: 2,
                px: 4,
                flexDirection: language === 'he' ? 'row-reverse' : 'row',
              }}
            >
              {t('home.hero.cta')}
            </GradientButton>
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3
          }}>

            {!user && (<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
              <Typography variant="h6" sx={{ ml: 0, fontSize: '0.8rem', color: alpha(theme.palette.common.white, 0.8) }}>
                {t('home.hero.haveAccount')}
              </Typography>
              <Button
                color="inherit"
                size="large"
                onClick={() => navigate('/login')}
                endIcon={
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: alpha(theme.palette.common.white, 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    mx: 2,
                    justifyContent: 'center'
                  }}>
                    <Login />
                  </Box>
                }
                sx={{
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { color: theme.palette.warning.main }
                }}
              >
                {t('navigation.login')}
              </Button>
            </Box>
            )}

            {showDemo && <Button
              color="inherit"
              size="large"
              onClick={() => setIsDemoModalOpen(true)}
              startIcon={
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: alpha(theme.palette.common.white, 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PlayArrow />
                </Box>
              }
              sx={{
                textTransform: 'none',
                fontSize: '1.1rem',
                '&:hover': { color: theme.palette.warning.main }
              }}
            >
              {t('home.hero.watchDemo')}
            </Button>}
          </Box>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10, position: 'relative', zIndex: 1 }} id="features">
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '2.5rem' },
            fontWeight: 'bold',
            mb: 8,
            background: `linear-gradient(45deg, ${theme.palette.info.light}, ${theme.palette.primary.light})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          {t('home.features.title')}
        </Typography>

        <Grid container spacing={4}>
          {[
            { icon: <AutoAwesome />, title: t('home.features.ai.title'), desc: t('home.features.ai.desc'), color: theme.palette.warning.main },
            { icon: <Star />, title: t('home.features.personalized.title'), desc: t('home.features.personalized.desc'), color: theme.palette.info.main },
            { icon: <LightMode />, title: t('home.features.daily.title'), desc: t('home.features.daily.desc'), color: theme.palette.secondary.main }
          ].map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeatureCard sx={{ height: '100%', p: 4 }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      background: `linear-gradient(45deg, ${feature.color}, ${alpha(feature.color, 0.7)})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      transition: 'transform 0.3s ease',
                      '&:hover': { transform: 'rotate(12deg)' }
                    }}
                  >
                    {React.cloneElement(feature.icon, {
                      sx: { fontSize: 32, color: 'common.black' }
                    })}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                    {feature.desc}
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: 10, position: 'relative', zIndex: 1 }} id="pricing">
        <Typography
          variant="h2"
          align="center"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
            fontWeight: 'bold',
            mb: 8,
            background: `linear-gradient(45deg, ${theme.palette.success.light}, ${theme.palette.info.light})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          {t('home.pricing.title')}
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {[
            {
              title: t('home.pricing.free.title'),
              price: t('home.pricing.free.price'),
              features: t('home.pricing.free.features', { returnObjects: true }) as string[],
              featured: false
            },
            {
              title: t('home.pricing.pro.title'),
              price: t('home.pricing.pro.price'),
              features: t('home.pricing.pro.features', { returnObjects: true }) as string[],
              featured: true
            },
            {
              title: t('home.pricing.elite.title'),
              price: t('home.pricing.elite.price'),
              features: t('home.pricing.elite.features', { returnObjects: true }) as string[],
              featured: false
            }
          ].map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <PricingCard featured={plan.featured} sx={{ height: '100%', p: 4 }}>
                {plan.featured && (
                  <Chip
                    label={t('home.pricing.mostPopular')}
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                      color: 'common.black',
                      fontWeight: 'bold'
                    }}
                  />
                )}
                <CardContent>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {plan.title}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 4 }}>
                    {plan.price}
                  </Typography>
                  <Box sx={{ mb: 4 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <Box key={featureIndex} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Check sx={{ color: 'success.main', fontSize: 20, mr: 2 }} />
                        <Typography sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    fullWidth
                    onClick={() => handleStartPlan(index)}
                    size="large"
                    variant={plan.featured ? "contained" : "outlined"}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      ...(plan.featured && {
                        background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                        color: 'common.black',
                        '&:hover': {
                          background: `linear-gradient(45deg, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
                        }
                      })
                    }}
                  >
                    {index === 0 ? t('home.pricing.getStarted') : index === 1 ? t('home.pricing.startPro') : t('home.pricing.goElite')}
                  </Button>
                </CardContent>
              </PricingCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Testimonials */}
      <Container maxWidth="md" sx={{ py: 10, position: 'relative', zIndex: 1 }}>
        <Paper
          sx={{
            background: `linear-gradient(135deg, 
              ${alpha(theme.palette.primary.main, 0.3)} 0%, 
              ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            borderRadius: 4,
            p: 6,
            textAlign: 'center'
          }}
        >
          <Box sx={{ mb: 4 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} sx={{ color: theme.palette.warning.main, fontSize: 24, mx: 0.5 }} />
            ))}
          </Box>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              fontStyle: 'italic',
              lineHeight: 1.6,
              color: alpha(theme.palette.common.white, 0.9)
            }}
          >
            "{testimonials[currentTestimonial].text}"
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.primary.light, 0.8),
              fontWeight: 600
            }}
          >
            — {testimonials[currentTestimonial].author}
          </Typography>
        </Paper>
      </Container>

      {/* Final CTA Section */}
      <Container maxWidth="md" sx={{ py: 10, position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 'bold',
            mb: 3,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
          }}
        >
          {t('home.finalCta.title')}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 6,
            color: alpha(theme.palette.common.white, 0.8),
            lineHeight: 1.6
          }}
        >
          {t('home.finalCta.description')}
        </Typography>
        <GradientButton
          onClick={() => navigate('/onboarding')}
          size="large"
          sx={{
            fontSize: '1.3rem',
            py: 2,
            px: 6,
            boxShadow: `0 10px 30px ${alpha(theme.palette.warning.main, 0.4)}`
          }}
        >
          {t('home.finalCta.button')}
        </GradientButton>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          py: 6,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Star sx={{ color: theme.palette.warning.main, fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('brand.name')}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.primary.light, 0.8)
              }}
            >
              {t('home.footer.copyright')}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Demo Modal */}
      <DemoModal
        open={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
      />
    </>
  );
};

export default Home;